import { ApiOkResponse } from 'apisauce';
import { MouseEvent, useEffect } from 'react';
import { useUserContextPreferences } from '../../../../../hooks/useUserContextPreferences';
import { FilterPreferenceRecognizedLists } from '../../../../../services/modules/userContextPreferences/userContextPreferences.types';
import { Pagination } from '../../../../../types/pagination.type';
import useDynamicOptionsSelectorsCreator from '../../../../Crud/filters/components/DynamicOptionsSelectorsCreator/hooks/useDynamicOptionsSelectorsCreator';
import useMultiDynamicOptionsSelector from '../../../../Crud/filters/hooks/multiOptionsSelectorFilter/useMultiDynamicOptionsSelector/useMultiDynamicOptionsSelector';
import {
  FilterProps,
  MultiDynamicOptionsCreatorFilter,
  MultiDynamicOptionsFilter,
  PossibleFilters,
} from '../../../../Crud/filters/sharedBetweenFilters/types';
import DynamicFilterFactory from '../../../factories/helpers/dynamic-filter.helper';
import SelectedFilterChip from './SelectedFilterChip';

export interface SelectedChipCreatorProps {
  filter: MultiDynamicOptionsCreatorFilter;
  filterKey: string;
  data: Record<string, any>;
  onClick: (event: MouseEvent<HTMLElement>, filter: FilterProps) => void;
  list?: FilterPreferenceRecognizedLists;
}

const SelectedChipCreator = ({
  data,
  filter,
  filterKey,
  list,
  onClick,
}: SelectedChipCreatorProps) => {
  const { getDefaultFiltersToList } = useUserContextPreferences();
  const defaultFilters = list ? getDefaultFiltersToList(list) : null;

  const { counterToClearAll } = useDynamicOptionsSelectorsCreator({ list });

  const { keys, hookArgs, dynamicOptionQueryFn, dynamicOptionQueryKey } =
    filter.multiDynamicOptionsCreatorArgs;

  const title = data[keys.filterTitle as string];
  const filterLogic = useMultiDynamicOptionsSelector<
    any,
    ApiOkResponse<Pagination<Record<string, any>>>
  >({
    ...hookArgs,
    key: filterKey,
    debounceTime: 15,
    defaultValues: defaultFilters?.[filterKey],
    queryFn: (params) =>
      dynamicOptionQueryFn({
        ...params,
        [keys.keyToRequestOptions as string]: data[keys.filterId as string],
      }),
    queryKeyFn: (params) =>
      dynamicOptionQueryKey({
        ...params,
        [keys.keyToRequestOptions as string]: data[keys.filterId as string],
      }),
  });

  const multiDynamicOptionsFilter: MultiDynamicOptionsFilter = {
    type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
    title: title,
    label: title,
    multiDynamicOptionsArgs: filterLogic,
    cleanFilter: filterLogic.cleanFilter,
    getSearchParamKeys: filter.getSearchParamKeys,
  };

  useEffect(() => {
    if (counterToClearAll <= 0) {
      return;
    }

    filterLogic.setSelectedOptions([]);
  }, [counterToClearAll, filterLogic.setSelectedOptions]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const loadSelectedOptions = async () => {
      if (filterLogic.selectedOptions?.length > 0) {
        filterLogic.handleLoadSpecifOptions(filterLogic.selectedOptions);
      }
    };

    loadSelectedOptions();
  }, []);

  const factory = new DynamicFilterFactory(multiDynamicOptionsFilter);

  return (
    <SelectedFilterChip filter={multiDynamicOptionsFilter} factory={factory} onClick={onClick} />
  );
};

export default SelectedChipCreator;
